import React, { useEffect, useState } from 'react'
import Topic from './Topic'
import useAxios from 'hooks/useAxios'
import { toast } from 'libs/toast'
import uuid from 'uuid/v4'
import useSaveUserActivityLog, { ANSWER_TOPIC_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import * as R from 'ramda'
import constants from 'config/constants'
import usePermission from 'hooks/usePermission'

const TopicContainer = () => {
  // Important note!
  // Use `is_predefined` field to check when to post/patch
  // post → is_predefined = true (Does not exist in topic model)
  // patch → is_predefined = false (Exist in topic model)
  // Use `source` field to show on UI
  const saveUserActivityLog = useSaveUserActivityLog()

  const [tabName, setTabName] = useState('topic')
  const [topicState, setTopicState] = useState([])
  const [topicGroupState, setTopicGroupState] = useState([])
  const { hasPermission } = usePermission()

  const { data: topicData, loading: topicDataLoading } = useAxios({
    method: 'get',
    url: 'responses/topics/',
  })

  const { data: topicGroupData, loading: topicGroupDataLoading } = useAxios({
    method: 'get',
    url: 'responses/topic-groups/',
  })

  const { fetch: updateTopic, loading: updateTopicLoading } = useAxios({
    method: 'patch',
    skip: true,
  })

  const { fetch: createTopic, loading: createTopicLoading } = useAxios({
    method: 'post',
    url: 'responses/topics/',
    skip: true,
  })

  const { data: canCreateTopic, loading: checkingCreatePermission } = useAxios({
    url: 'billing/plans/feature-toggle/',
    method: 'post',
    data: { feature: 'custom_topic_creation' },
  })

  useEffect(() => {
    if (topicGroupData) {
      const transformedResults = topicGroupData.results.map((item) => ({
        ...item,
        key: uuid(),
      }))

      setTopicGroupState(transformedResults)
    }

    if (topicData) {
      const transformedResults = topicData.results.map((item) => ({
        ...item,
        key: uuid(),
      }))

      setTopicState(transformedResults)
    }
  }, [topicGroupData, topicData])

  const handleSwitchTab = (type) => {
    setTabName(type)
  }

  const moveTopicByStatus = (topicResponse, topicDataRow, responseStatus, data) => {
    if (topicResponse.status === 'active') {
      const index = topicState.findIndex((topic) => topic.key === topicDataRow.key)

      if (index !== -1) {
        setTopicState((prev) => {
          return R.update(index, { ...topicResponse, key: uuid() }, prev)
        })

        toast.success('Topic visibility turned on!')
        saveUserActivityLog({
          action: ANSWER_TOPIC_ACTION_NAME.activate_topic,
          statusCode: responseStatus,
          requestBody: data,
          target: `topic_${topicDataRow.id}`,
        })
      }
    } else {
      const index = topicState.findIndex((topic) => topic.key === topicDataRow.key)
      if (index !== -1) {
        setTopicState((prev) => {
          return R.update(index, { ...topicResponse, key: uuid() }, prev)
        })

        toast.success('Topic visibility turned off.')
        saveUserActivityLog({
          action: ANSWER_TOPIC_ACTION_NAME.deactivate_topic,
          statusCode: responseStatus,
          requestBody: data,
          target: `topic_${topicDataRow.id}`,
        })
      }
    }
  }

  const handleToggleActive = (topicDataRow) => {
    if (topicDataRow.is_predefined) {
      // is_predefined true means not exist in Topic model
      const data = {
        name: topicDataRow.name,
        description: topicDataRow.description,
        is_predefined: topicDataRow.is_predefined,
        groups: topicDataRow.groups,
        status: topicDataRow.status === 'inactive' ? 'active' : 'inactive',
      }

      createTopic({ data: data })
        .then((res) => {
          if (res.data) {
            moveTopicByStatus(res.data, topicDataRow, res.status, data)
          }
        })
        .catch((err) => {
          console.error(err)
          saveUserActivityLog({
            action: ANSWER_TOPIC_ACTION_NAME.activate_topic,
            statusCode: err.response.status,
            errorResponseBody: err.response.data,
            requestBody: data,
            target: `topic_${topicDataRow.id}`,
          })
          if (R.path(['response', 'status'], err) === 400) {
            toast.error(err.response.data.detail)
          } else {
            toast.error(
              'Uh oh. There was a problem activating the new topic. Please try again or contact Simplesat support.'
            )
          }
        })
    } else {
      const data = {
        ...topicDataRow,
        status: topicDataRow.status === 'inactive' ? 'active' : 'inactive',
      }

      updateTopic({ url: `responses/topics/${topicDataRow.id}/`, data: data })
        .then((res) => {
          if (res.data) {
            moveTopicByStatus(res.data, topicDataRow, res.status, data)
          }
        })
        .catch((err) => {
          console.error(err)
          saveUserActivityLog({
            action: ANSWER_TOPIC_ACTION_NAME.deactivate_topic,
            statusCode: err.response.status,
            errorResponseBody: err.response.data,
            requestBody: data,
            target: `topic_${topicDataRow.id}`,
          })

          if (R.path(['response', 'status'], err) === 400) {
            toast.error(err.response.data.detail)
          } else {
            toast.error(
              'Uh oh. There was a problem deactivating the new topic. Please try again or contact Simplesat support.'
            )
          }
        })
    }
  }

  return (
    <Topic
      topicDataLoading={topicDataLoading}
      topicGroupDataLoading={topicGroupDataLoading}
      topicData={topicState}
      topicGroupData={topicGroupState}
      handleSwitchTab={handleSwitchTab}
      tabName={tabName}
      handleToggleActive={handleToggleActive}
      setTopicState={setTopicState}
      setTopicGroupState={setTopicGroupState}
      allowAdminOrAbove={hasPermission(constants.permissions.manageTopicSettings)}
      updateTopicLoading={updateTopicLoading}
      createTopicLoading={createTopicLoading}
      canCreateTopic={R.path(['enabled'], canCreateTopic)}
      checkingCreatePermission={checkingCreatePermission}
    />
  )
}

export default TopicContainer



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/Topics/container.js